import React, { useEffect, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { Close } from '@codex/organisms';
import PropTypes from 'prop-types';
import { Tooltip } from '@codex/basics';
import { Icon } from '@codex/icons';
import uiLocale from '../../../../utils/UILocale.js';

const translations = {
  copyLink: uiLocale.getString('component.comparison.url')
};

const Title = styled.h2`
  color: ${({ theme }) => theme.colors.shades[0]};
  margin: 0;
  font-size: 23px;
`;

const TitleCont = styled.div`
  color: ${({ theme }) => theme.colors.shades[0]};
  text-align: center;
  position: sticky;
  top: 0;
  z-index: 4;
  padding: 20px 0;
  background: ${({ theme }) => theme.colors.shades[7]};
`;

const slideInOrOut = (isSlideIn) => keyframes`
  from {
    -webkit-transform: translate3d(0, ${isSlideIn ? '100%' : 0}, 0);
    transform: translate3d(0, ${isSlideIn ? '100%' : 0}, 0);
    visibility: visible;
  }

  to {
    -webkit-transform: translate3d(0, ${isSlideIn ? 0 : '100%'}, 0);
    transform: translate3d(0, ${isSlideIn ? 0 : '100%'}, 0);
    visibility: hidden;
  }
`;

const Container = styled.div`
  height: 80%;
  width: 90%;
  padding: 0 5%;
  overflow: hidden;
  overflow-y: auto;
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: ${({ theme }) => theme.colors.shades[7]};
  box-shadow: 0 -3px 9px 0 rgba(0,0,0,0.08);
  animation: ${(props) => slideInOrOut(props.isSlideIn)} ease ${(props) => props.duration}s;
`;

const ContentCont = styled.div`
  background: ${({ theme }) => theme.colors.shades[7]};
`;

const CopyLink = styled.div`
  color: ${({ theme }) => theme.colors.link.default};
  position: absolute;
  left: 0;
  top: 50%;
  transform: translate(0, -50%);
  display: inline-block;
  cursor: pointer;
  border-radius: 50%;
  text-decoration: underline;
`;

const CloseCont = styled.div`
  background: ${({ theme }) => theme.colors.link.default};
  position: absolute;
  right: 0;
  top: 50%;
  transform: translate(0, -50%);
  display: inline-block;

  color: ${({ theme }) => theme.colors.shades[7]};
  cursor: pointer;
  border-radius: 50%;
`;

const Fade = styled.div`
  overflow: visible;
  z-index: 9999;
  position: fixed;
  padding: 0;
  margin: 0;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(34, 34, 34, 0.6);
`;

const InputTextCopyCont = styled.input`
  position: absolute;
  left: -9999px;
  top: -9999px;
`;

const TooltipCon = styled.div`
  width: 205px;
  position: absolute;
  top: 78%;
  left: -60px;
  padding-top: 10px;
  z-index: 4;
`;

const GreenTickIcon = styled(Icon)`
  color: green;
`;

const CopyIcon = styled(Icon)`
  margin-left: 10px;
`;

const BottomPopup = ({
  visible, setVisible, children, title, duration
}) => {

  let timer = null;
  const [isTooltipOn, setIsTooltipOn] = useState(false);
  const [isSlideIn, setisSlideIn] = useState(true);
  const copyText = useRef(null);

  useEffect(() => {
    document.body.style.overflowY = 'hidden';
    document.querySelector('#header').style.zIndex = 1;

    return (() => {
      document.querySelector('#header').style.zIndex = 101;
      clearTimeout(timer);
    });
  }, []);

  const animateAndHide = () => {
    setisSlideIn(false);

    timer = setTimeout(() => {
      setVisible(false);
      setisSlideIn(true);
      document.body.style.overflowY = 'unset';
    }, duration * 1000);
  };

  const handleHide = () => {
    animateAndHide();
  };

  const handleTooltipOn = () => {
    setIsTooltipOn(true);

    if (!isTooltipOn) {
      timer = setTimeout(() => {
        setIsTooltipOn(false);
      }, 3000);
    }
  };

  const handleCopyLink = () => {
    // build copy link URL
    handleTooltipOn();
    let copyLink = `${window.location.origin}/gbuk/s_action/compare/`;
    let products = JSON.parse(window.sessionStorage.getItem('state'));
    products = products.listingPage.comparison.products;
    products = products[Object.keys(products)[0]];
    products.forEach((product, i) => {
      if (i === products.length - 1) {
        copyLink += `${product.id}.html`;
      } else {
        copyLink += `${product.id}-`;
      }
    });
    // copy to the clipboard
    copyText.current.value = copyLink;
    copyText.current.select();
    copyText.current.setSelectionRange(0, 99999); /* For mobile device */
    document.execCommand('copy');
  };

  const handleClickOutside = (e) => {
    if (e.target.id === 'fade') {
      animateAndHide();
    }
  };

  const renderTooltip = () => (
    isTooltipOn && (
      <TooltipCon>
        <Tooltip
          fadeInOut
          small
          type={'positive'}
          arrow={'top'}
          arrowMobile={'bottom'}
          textAlign={'center'}
        >
          <GreenTickIcon icon={'Tick'} />
          {' '}
          Item copied to clipboard!
        </Tooltip>
      </TooltipCon>
    )
  );

  const handleScroll = () => {
    const containerDiv = document.getElementById('container-div');
    const containerChildDiv = document.getElementById('container-child-div');
    if (containerDiv && containerChildDiv) {

      let scrollTop = 0;
      if (containerDiv.scrollTop > 0) {
        scrollTop = containerDiv.scrollTop / 10;
      }

      containerDiv.style.height = `${Math.min(100, 80 + scrollTop)}%`;
      containerChildDiv.style.marginTop = `${Math.min(200, containerDiv.scrollTop)}px`;
    }
  };

  return (
    <div>
      {
          visible && (
          <Fade id={'fade'} onClick={handleClickOutside}>
            <Container
              id={'container-div'}
              onScroll={handleScroll}
              isSlideIn={isSlideIn}
              duration={duration}
            >
              <div id={'container-child-div'}>
                <TitleCont id={'title-con'}>
                  <InputTextCopyCont ref={copyText} onClick={handleCopyLink} />
                  <CopyLink onClick={handleCopyLink}>
                    {
                      // added default operator for testing purpose
                    }
                    {translations.copyLink || 'Copy Link'}
                    <CopyIcon icon={'Copy'} size={'large'} />
                    {renderTooltip()}
                  </CopyLink>
                  <Title>{title}</Title>
                  <CloseCont id={'close'} className={'close-cont'} onClick={handleHide}>
                    <Close />
                  </CloseCont>
                </TitleCont>
                <ContentCont>
                  {children}
                </ContentCont>
              </div>
            </Container>
          </Fade>
          )
}
    </div>
  );
};

BottomPopup.propTypes = {
  visible: PropTypes.bool.isRequired,
  setVisible: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
  title: PropTypes.string.isRequired,
  duration: PropTypes.number
};

BottomPopup.defaultProps = {
  duration: 0.5
};

export default BottomPopup;
